"use client";

import { createElement, type ComponentType } from "react";
import clsx from "clsx";
import {
  Dialog,
  DialogBackdrop,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/ui";
import { getCandidateCardComponent } from "@lib/floats/candidateCardVariants";
import { THEME_TYPES } from "@lib/enums";
import Footer from "@components/Floats/Share/parts/Email/Footer";
import styles from "./ThemePreviewDialog.module.css";

const THEME_BACKGROUNDS: Record<string, string> = {
  [THEME_TYPES.SNOW]: "#EBEDEF",
  [THEME_TYPES.LIGHT]: "transparent",
  [THEME_TYPES.STARTUP]: "transparent",
};

const PREVIEW_CANDIDATE = {
  id: "theme-preview",
  name: "Alex Morgan",
  position_title: "Senior Product Designer",
  highlights: "10+ years shaping consumer products at high-growth startups.",
  email_heading: "Why Alex is a great fit",
  email_summary:
    "Alex has led design systems and end-to-end product work at Stripe and Airbnb, with a focus on consumer-facing experiences. They are based in San Francisco and open to senior IC or staff roles, ideally hybrid.",
  avatar:
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23e0e0e0'/%3E%3Ccircle cx='100' cy='80' r='40' fill='%23bdbdbd'/%3E%3Cellipse cx='100' cy='170' rx='60' ry='50' fill='%23bdbdbd'/%3E%3C/svg%3E",
  skills: [
    { id: 1, name: "Figma", type: "tool" },
    { id: 2, name: "Design Systems", type: "tool" },
    { id: 3, name: "Prototyping", type: "tool" },
    { id: 4, name: "User Research", type: "tool" },
    { id: 5, name: "Accessibility", type: "tool" },
  ],
};

const SAMPLE_INTRODUCTION =
  "Hi {Contact}, I wanted to share a candidate I think you'll be excited about. Take a look below — happy to make an intro if it's a fit.";

const SAMPLE_CONCLUSION =
  "Let me know if you'd like to set up a call. I can also share more candidates with similar backgrounds if helpful.";

/**
 * Resolves and renders the candidate-card variant for the given theme. Hoisted
 * to module scope so the variant component isn't (re)created during the parent's
 * render (react-hooks/static-components).
 */
function CandidateCardRenderer({
  themeType,
  ...props
}: {
  themeType: string;
  [key: string]: unknown;
}) {
  // The variants share a shape but not a type, so the props ride through as-is.
  const Component = getCandidateCardComponent(themeType) as unknown as
    ComponentType<Record<string, unknown>>;
  return createElement(Component, props);
}

export interface ThemePreviewDialogProps {
  open: boolean;
  themeType: string | null;
  recruiter: Record<string, unknown>;
  onClose: () => void;
}

export default function ThemePreviewDialog({
  open,
  themeType,
  recruiter,
  onClose,
}: ThemePreviewDialogProps) {
  if (!themeType) return null;

  const bgColor = THEME_BACKGROUNDS[themeType];

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>Email preview</DialogTitle>
            <DialogClose aria-label="Close preview" />
          </DialogHeader>
          <DialogBody>
            <div className={styles.frame}>
              <div className={styles.email}>
                <p className={styles.paragraph}>{SAMPLE_INTRODUCTION}</p>
                <div
                  className={clsx(
                    styles.candidate,
                    bgColor === "transparent" && styles.candidateFlush,
                  )}
                  style={bgColor ? { backgroundColor: bgColor } : undefined}
                >
                  <p className={styles.heading}>
                    {PREVIEW_CANDIDATE.email_heading}
                  </p>
                  <p className={styles.paragraph}>
                    {PREVIEW_CANDIDATE.email_summary}
                  </p>
                  <CandidateCardRenderer
                    themeType={themeType}
                    candidate={PREVIEW_CANDIDATE}
                    recruiterLogoUrl={recruiter?.logo_url}
                    recruiterName={recruiter?.name}
                  />
                </div>
                <p className={styles.paragraph}>{SAMPLE_CONCLUSION}</p>
                <Footer recruiter={recruiter} />
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
